import { ThemeEvents } from '@theme/events';

class FitFeaturedProduct extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.addEventListener('click', this.handleClick, { signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.handleVariantUpdate, { signal });
    const activeSlide = this.querySelector('[data-media-id]:not([hidden])');
    if (activeSlide) this.showMedia(activeSlide.dataset.mediaId);
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.querySelectorAll('video').forEach(video => video.pause());
  }

  showMedia(id) {
    const slides = Array.from(this.querySelectorAll('[data-media-id]'));
    if (!slides.some(slide => slide.dataset.mediaId === String(id))) return;
    for (const slide of slides) {
      slide.hidden = slide.dataset.mediaId !== String(id);
      if (slide.hidden) {
        slide.querySelectorAll('video').forEach(video => video.pause());
        // Reload external embeds only when playing media leaves the visible slide.
        slide.querySelectorAll('iframe').forEach(frame => {
          if (frame.dataset.wasVisible === 'true') {
            frame.src = frame.src;
            delete frame.dataset.wasVisible;
          }
        });
        slide.querySelector('model-viewer')?.pause?.();
      } else {
        slide.querySelectorAll('iframe').forEach(frame => { frame.dataset.wasVisible = 'true'; });
      }
    }
    this.querySelectorAll('[data-thumbnail]').forEach(button => {
      const active = button.dataset.thumbnail === String(id);
      button.setAttribute('aria-pressed', String(active));
      if (active) {
        const track = button.parentElement;
        const buttonBounds = button.getBoundingClientRect();
        const trackBounds = track.getBoundingClientRect();
        if (buttonBounds.left < trackBounds.left || buttonBounds.right > trackBounds.right) {
          track.scrollLeft += buttonBounds.left - trackBounds.left;
        }
      }
    });
  }

  handleClick = async (event) => {
    const thumbnail = event.target.closest('[data-thumbnail]');
    if (thumbnail) this.showMedia(thumbnail.dataset.thumbnail);
    const arrow = event.target.closest('[data-gallery-step]');
    if (arrow) {
      const slides = Array.from(this.querySelectorAll('[data-media-id]'));
      const current = slides.findIndex(slide => !slide.hidden);
      const next = (current + Number(arrow.dataset.galleryStep) + slides.length) % slides.length;
      if (slides[next]) this.showMedia(slides[next].dataset.mediaId);
    }
    const copy = event.target.closest('[data-copy-link]');
    if (copy) {
      const status = this.querySelector('[data-copy-status]');
      try {
        await navigator.clipboard.writeText(copy.dataset.url);
        if (status) {
          status.classList.remove('visually-hidden');
          status.textContent = 'Link copied!';
          clearTimeout(this._copyTimeout);
          this._copyTimeout = setTimeout(() => {
            status.classList.add('visually-hidden');
            status.textContent = '';
          }, 2500);
        }
      } catch {
        if (status) {
          status.classList.remove('visually-hidden');
          status.textContent = `Copy: ${copy.dataset.url}`;
        }
      }
    }
  };

  handleVariantUpdate = (event) => {
    const { resource, data } = event.detail;
    const fresh = data.html.querySelector('fit-featured-product');
    if (!fresh) return;
    const quantity = this.querySelector('input[name="quantity"]');
    const newQuantity = fresh.querySelector('input[name="quantity"]');
    if (quantity && newQuantity) quantity.disabled = newQuantity.disabled;
    const stock = this.querySelector('[data-stock]');
    const newStock = fresh.querySelector('[data-stock]');
    if (stock && newStock) stock.textContent = newStock.textContent;
    const link = this.querySelector('[data-product-link]');
    const newLink = fresh.querySelector('[data-product-link]');
    if (link && newLink) link.href = newLink.href;
    if (resource?.featured_media?.id) this.showMedia(resource.featured_media.id);
  };
}

if (!customElements.get('fit-featured-product')) {
  customElements.define('fit-featured-product', FitFeaturedProduct);
}
